import { useMemo, useState } from 'react';

import { usePersonsList } from '../../../hooks/useApiQueries';
import { filterPersons, sortPersons } from '../utils';
import { Person, PersonSortColumn, SortOrder } from '../types';

export const usePersons = () => {
  const { data: persons = [], isLoading, refetch } = usePersonsList();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<PersonSortColumn>('id');
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleSort = (column: PersonSortColumn) => {
    const isAsc = sortColumn === column && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortColumn(column);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredAndSorted = useMemo(() => {
    const filtered = filterPersons(persons as Person[], searchQuery);
    return sortPersons(filtered, sortColumn, sortOrder);
  }, [persons, searchQuery, sortColumn, sortOrder]);

  const paginated = useMemo(() => {
    return filteredAndSorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAndSorted, page, rowsPerPage]);

  return {
    persons: paginated,
    totalCount: filteredAndSorted.length,
    totalRawCount: (persons as Person[]).length,
    isLoading,
    searchQuery,
    sortColumn,
    sortOrder,
    page,
    rowsPerPage,
    handleSort,
    handleSearchChange,
    handlePageChange,
    handleRowsPerPageChange,
    refetch,
  };
};

